import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import PageHead from "../components/PageHead";
import { Reveal, SectionHead } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { IMAGES, SITE } from "../lib/site";

const VALUES = [
    {
        title: "Reliability",
        desc: "A trailer you can plan around — placed when promised, on the right route.",
    },
    {
        title: "Timely Placement",
        desc: "Vehicle placement and transit timelines we commit to and keep.",
    },
    {
        title: "Transparent Rates",
        desc: "Competitive freight rates, stated upfront — no layered commissions.",
    },
    {
        title: "Professional Service",
        desc: "Owner-led and accountable, from first call to final delivery.",
    },
];

const TIMELINE = [
    {
        label: "2022",
        title: "The Beginning",
        desc: "OSM Cargo Movers — One Stop Movers — is established by Anand Dubey in Kalamboli, Navi Mumbai.",
    },
    {
        label: "Pan India",
        title: "The Network Grows",
        desc: "From Steel Market Road to every major corridor — 40ft, 50ft and 60ft trailer supply across India.",
    },
    {
        label: "Today",
        title: "One Stop Movers",
        desc: "One call for trailer availability, ODC & loose cargo movement and long-distance transportation.",
    },
];

export default function About() {
    return (
        <>
            <PageHead
                eyebrow="About Us"
                title="One Stop Movers, since 2022"
                hindi="About us — who we are and how we work"
                intro="Established in 2022 and led by Anand Dubey, OSM Cargo Movers is a Pan India trailer supplier built on one simple promise: reliable vehicles, competitive rates and timely transportation."
                testid="about-page"
            />

            <section className="pb-20 lg:pb-28" data-testid="about-story-section">
                <div className="container-x grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <Reveal className="lg:sticky lg:top-28">
                            <div className="overflow-hidden rounded-3xl">
                                <img
                                    src={IMAGES.port}
                                    alt="Container terminal — trailer and cargo operations"
                                    loading="lazy"
                                    className="aspect-[4/5] w-full object-cover"
                                />
                            </div>
                            <a
                                href={SITE.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="about-hq-map-link"
                                className="card mt-5 flex items-start gap-3 p-5 transition-colors hover:border-ink"
                            >
                                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                                <span>
                                    <span className="block text-sm font-bold text-ink">
                                        Headquarters — Kalamboli, Navi Mumbai
                                    </span>
                                    <span className="mt-1 block text-sm text-steel">
                                        {SITE.address}
                                    </span>
                                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand">
                                        Open in Google Maps
                                        <ArrowUpRight className="h-3.5 w-3.5" />
                                    </span>
                                </span>
                            </a>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="text-lg leading-relaxed text-steel sm:text-xl">
                                OSM Cargo Movers — <strong className="font-semibold text-ink">One Stop Movers</strong> — was
                                established in <strong className="font-semibold text-ink">2022</strong> by{" "}
                                <strong className="font-semibold text-ink">Anand Dubey</strong> at
                                Kalamboli, Navi Mumbai. From Steel Market Road — India's logistics
                                heartland — we supply <strong className="font-semibold text-ink">40ft, 50ft and 60ft
                                trailers</strong> across the country.
                            </p>
                            <p className="mt-6 text-base leading-relaxed text-steel">
                                Our work is simple to describe and demanding to do well: place the
                                right trailer at the right ramp at the right hour. Over-dimensional
                                and loose cargo, steel and machinery, containers and break-bulk —
                                moved over long distances, on schedule, at competitive rates.
                            </p>
                            <p className="mt-4 font-hindi text-base leading-relaxed text-steel">
                                Our promise is simple — the right trailer, at the right time, at the right price.
                            </p>
                        </Reveal>

                        <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="about-values-grid">
                            {VALUES.map((v, i) => (
                                <Reveal key={v.title} delay={i * 0.06} className="h-full">
                                    <div className="card h-full p-6 transition-colors hover:border-ink">
                                        <p className="font-display text-lg font-bold uppercase tracking-tight text-ink">
                                            {v.title}
                                        </p>
                                        <p className="mt-2 text-sm leading-relaxed text-steel">
                                            {v.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-sand py-20 lg:py-28" data-testid="about-timeline-section">
                <div className="container-x">
                    <SectionHead
                        eyebrow="Our Journey"
                        title="From Kalamboli to every corridor"
                        hindi="Started in 2022 — serving all of India today"
                    />
                    <div className="mt-14 grid gap-10 md:grid-cols-3" data-testid="about-timeline">
                        {TIMELINE.map((t, i) => (
                            <Reveal key={t.label} delay={i * 0.1}>
                                <div className="relative border-l-2 border-brand/30 pl-6">
                                    <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-brand" />
                                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand">
                                        {t.label}
                                    </p>
                                    <p className="mt-3 font-display text-xl font-bold uppercase tracking-tight text-ink">
                                        {t.title}
                                    </p>
                                    <p className="mt-2 text-sm leading-relaxed text-steel">{t.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28" data-testid="about-pledge-section">
                <div className="container-x">
                    <Reveal>
                        <div className="rounded-3xl bg-navy p-8 sm:p-12 lg:p-16" data-testid="about-pledge-card">
                            <p className="eyebrow">Owner's Pledge</p>
                            <blockquote className="mt-6 max-w-3xl font-display text-2xl font-bold leading-snug tracking-tight text-paper sm:text-3xl lg:text-4xl">
                                “Competitive rates, reliable vehicle placement, timely transportation
                                and professional service — on every lane, every time.”
                            </blockquote>
                            <p className="mt-6 font-hindi text-base text-paper/70">
                                Your trust, our service.
                            </p>
                            <div className="mt-6 flex items-center gap-3.5">
                                <img
                                    src="/owner-anand-dubey.jpg"
                                    alt="Anand Dubey — Founder, OSM Cargo Movers"
                                    loading="lazy"
                                    className="h-14 w-14 rounded-full border-2 border-brand object-cover"
                                />
                                <p className="text-sm font-semibold text-paper">
                                    Anand Dubey
                                    <span className="ml-2 font-normal text-paper/60">
                                        — Founder, OSM Cargo Movers
                                    </span>
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand title="Let's move your cargo" hindi="Have cargo to move? Get in touch today." />
        </>
    );
}
